import { Component, computed, input } from '@angular/core';
import { SkeletonModule } from '@openng/optimus-ui/skeleton';

/** Layout presets for common loading placeholders. */
export type SkeletonVariant = 'cards' | 'fields';

/**
 * Domain-agnostic inline loading skeleton.
 * Use `cards` for stacked content cards/lists, `fields` for label+value grids (e.g. overview panels).
 *
 * @example
 * ```html
 * <lib-skeleton [count]="3" [lines]="2" />
 * <lib-skeleton variant="fields" [count]="5" [columns]="5" [showHeader]="true" />
 * ```
 */
@Component({
  selector: 'lib-skeleton',
  standalone: true,
  imports: [SkeletonModule],
  templateUrl: './skeleton.component.html',
  styleUrl: './skeleton.component.scss',
  host: {
    '[class]': 'hostClass()',
    'aria-busy': 'true',
    'aria-live': 'polite'
  }
})
export class SkeletonComponent {
  /** `cards` = stacked panels; `fields` = label/value grid. */
  variant = input<SkeletonVariant>('cards');

  /**
   * For `cards`: number of cards.
   * For `fields`: number of field cells in the grid.
   */
  count = input(3);

  /** Text lines rendered inside each card (`cards` only). */
  lines = input(2);

  /** Grid column count (`fields` only). */
  columns = input(3);

  /** Renders a title bar above the content (panel header). */
  showHeader = input(false);

  /** Extra class(es) on the host. */
  styleClass = input('');

  /** Extra class(es) on each card / panel shell. */
  cardClass = input('');

  /** Cycled widths so stacked lines look natural rather than identical. */
  private static readonly lineWidths = ['72%', '48%', '64%', '56%', '80%'] as const;

  readonly items = computed(() => Array.from({ length: Math.max(0, this.count()) }, (_, i) => i));

  readonly lineItems = computed(() =>
    Array.from({ length: Math.max(1, this.lines()) }, (_, i) => ({
      index: i,
      width: SkeletonComponent.lineWidths[i % SkeletonComponent.lineWidths.length]
    }))
  );

  readonly hostClass = computed(() =>
    ['lib-skeleton', `lib-skeleton--${this.variant()}`, this.styleClass()].filter(Boolean).join(' ')
  );

  readonly resolvedCardClass = computed(() => ['lib-skeleton__card', this.cardClass()].filter(Boolean).join(' '));

  readonly gridTemplateColumns = computed(() => `repeat(${Math.max(1, this.columns())}, minmax(0, 1fr))`);
}
